import { computed } from "vue";
import type { NavigationMenuItem } from "@nuxt/ui";
import type { TermsGdpr } from "~/components/Form/Acorduri.vue";
import { isProblem, isValidation } from "@kodall/kodall-client/dist/types";

export function useNavigation() {
  const route = useRoute();
  const auth = useAuthStore();
  const safeRoles = auth.user?.roles ?? [];
  const kc = useSecurity().kc.value;

  const legal = ref<TermsGdpr | null>(null);
  const isStandalone = safeRoles.includes("Client Standalone");
  const isClient = safeRoles.includes("Client");
  const isOperator = safeRoles.includes("Operator");

  const getLegal = async () => {
    const result = await kc.workflow<TermsGdpr>(
      "GetAppConfig",
      "getTermsGdpr",
      {},
    );
    if (isProblem(result) || isValidation(result)) return;

    legal.value = result;
  }
  getLegal();
  
  // Base nav helpers
  const nav = {
    home: () => ({ title: "Acasă", path: "/" }),
    login: () => ({ title: "Logare", path: "/login" }),
    cereri: () => ({ title: "Listă cereri", path: "/cereri" }),
    istoric: () => ({ title: "Istoric cereri", path: "/istoric" }),
    documente: () => ({ title: "Documente emise", path: "/documente" }),
    facturi: () => ({ title: "Facturi", path: "/facturi" }),
    profil: () => ({ title: "Profilul meu", path: "/profil?tab=data" }),
    notifications: () => ({
      title: "Notificări",
      path: "/profil?tab=notifications",
    }),
    settings: () => ({ title: "Setări", path: "/profil/?tab=settings" }),
    statusCerere: () => ({ title: "Status cerere", path: "/status-cerere" }),
  };

  const { companySettings } = useCompanyData();

  const supportLinks = computed<NavigationMenuItem[]>(() => {
    // Folosim datele din backend sau valori default goale dacă încă se încarcă
    const phone = companySettings.value.telefon;
    const email = companySettings.value.email;

    return [
      {
        label: phone,
        to: `tel:${phone.replace(/\s/g, "")}`, // Scoatem spațiile pentru link-ul tel:
        icon: "i-lucide-phone",
      },
      {
        label: email,
        to: `mailto:${email}`,
        icon: "i-lucide-mail",
      },
    ];
  });

  const legalLinks = computed<NavigationMenuItem[]>(() => {
    return [
      {
        label: "GDPR",
        to: "/public/gdpr",
        icon: "i-lucide-shield-check",
        target: "_blank",
      },
      {
        label: "Termeni și condiții",
        to: "/public/terms",
        icon: "i-lucide-file-text",
        target: "_blank",
      },
    ];
  });

  // Reactive menu
  const links = computed<NavigationMenuItem[][]>(() => {
    if (isStandalone) {
      // Standalone sees only Cerereri
      return [
        [
          {
            label: nav.cereri().title,
            to: nav.cereri().path,
            icon: "i-lucide-file-text",
            onSelect: () => {}, // handle menu close if needed
          },
        ],
        supportLinks.value,
        legalLinks.value,
      ];
    }

    // Client users menu
    if (isClient) {
      return [
        [
          {
            label: nav.home().title,
            to: nav.home().path,
            icon: "i-lucide-house",
            onSelect: () => {},
          },
          {
            label: nav.cereri().title,
            to: nav.cereri().path,
            icon: "i-lucide-file-text",
            onSelect: () => {},
          },
          {
            label: nav.profil().title,
            to: nav.profil().path,
            icon: "i-lucide-user-round",
            onSelect: () => {},
          },
          {
            label: nav.istoric().title,
            to: nav.istoric().path,
            icon: "i-lucide-list-ordered",
            onSelect: () => {},
          },
          {
            label: nav.documente().title,
            to: nav.documente().path,
            icon: "i-lucide-files",
            onSelect: () => {},
          },
          {
            label: nav.facturi().title,
            to: nav.facturi().path,
            icon: "i-lucide-credit-card",
            onSelect: () => {},
          },
        ],
        supportLinks.value,
        legalLinks.value,
      ];
    }

    // Operator menu or fallback
    if (isOperator) {
      return [
        [
          {
            label: nav.home().title,
            to: nav.home().path,
            icon: "i-lucide-house",
            onSelect: () => {},
          },
          {
            label: nav.istoric().title,
            to: nav.istoric().path,
            icon: "i-lucide-list-ordered",
            onSelect: () => {},
          },
          {
            label: nav.profil().title,
            to: nav.profil().path,
            icon: "i-lucide-user-round",
            onSelect: () => {},
          },
        ],
        supportLinks.value,
        legalLinks.value,
      ];
    }

    return [];
  });

  const currentPage = computed(() => {
    const currentPath = route.path;
    return Object.values(nav)
      .map((fn) => fn())
      .find((item) => {
        const itemBasePath = item.path.split("?")[0];
        return itemBasePath === currentPath;
      });
  });

  return { links, nav, isStandalone, isClient, isOperator, currentPage, legalLinks };
}
